/**
 * EEOC Training Overdue Alert for Admin Dashboard
 * Shows users who are overdue or near deadline for EEOC training
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Shield, 
  AlertTriangle, 
  Clock, 
  XCircle,
  ChevronRight,
  Users,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export default function EEOCOverdueAlert({ className }) {
  const { tenant } = useAuth();
  const [overdueData, setOverdueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (tenant?.id) {
      fetchOverdueUsers();
    }
  }, [tenant?.id]);

  const fetchOverdueUsers = async () => {
    try {
      const { data } = await api.get(`/eeoc/admin/overdue-users?tenant_id=${tenant.id}`);
      if (data.total_pending > 0) {
        setOverdueData(data);
      }
    } catch (error) {
      console.log('EEOC overdue data not available');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !overdueData || overdueData.total_pending === 0) {
    return null;
  }

  const { overdue_users, urgent_users, total_pending } = overdueData;
  const hasOverdue = overdue_users.length > 0;

  return (
    <Card 
      className={cn(
        'border-2',
        hasOverdue ? 'border-red-200 dark:border-red-800' : 'border-orange-200 dark:border-orange-800',
        className
      )}
      data-testid="eeoc-overdue-alert"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-lg',
              hasOverdue ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange-100 dark:bg-orange-900/30'
            )}>
              <Shield className={cn(
                'h-5 w-5',
                hasOverdue ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'
              )} />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                EEOC Training Alert
                <Badge variant={hasOverdue ? "destructive" : "secondary"} className="text-xs">
                  {total_pending} Pending
                </Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                {hasOverdue 
                  ? `${overdue_users.length} user${overdue_users.length !== 1 ? 's' : ''} overdue`
                  : `${urgent_users.length} user${urgent_users.length !== 1 ? 's' : ''} approaching deadline`
                }
              </CardDescription>
            </div>
          </div>
          <Link to="/dashboard/eeoc">
            <Button variant="outline" size="sm">
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <ScrollArea className={expanded ? "h-64" : "h-32"}>
          <div className="space-y-2">
            {/* Overdue Users */}
            {overdue_users.slice(0, expanded ? undefined : 2).map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <Badge variant="destructive" className="text-xs">
                  {user.days_overdue} days overdue
                </Badge>
              </div>
            ))}
            
            {/* Urgent Users */}
            {urgent_users.slice(0, expanded ? undefined : (2 - Math.min(overdue_users.length, 2))).map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                  {user.days_remaining} days left
                </Badge>
              </div>
            ))}
          </div>
        </ScrollArea>
        
        {total_pending > 2 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full mt-2"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show Less' : `Show ${total_pending - 2} More`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
